import { useState, useMemo, useCallback } from 'react'
import type { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'reactflow'
import { Download, Check, Loader2 } from 'lucide-react'
import Modal from '../ui/Modal'
import { cn } from '../../lib/utils'
import { useProjectId } from '../../contexts/ProjectIdContext'
import { buildProjectAssetUrl, getAuthToken, getPublicationToken, getThumbnailUrl, createDebug } from '@ponpon/api-client'
import { buildDownloadCandidates, type CanvasAssetCandidate, type MediaType } from './nodes/shared/mentionUtils'
import { IMAGE_ICON, VIDEO_ICON } from './nodes/shared/ReferenceSelector'
import { AudioWaveformVisualization } from './nodes/shared/AudioWaveformVisualization'
import { toast } from 'sonner'
import {
  buildAssetDownloadFilename,
  buildClipDownloadFilename,
} from '../../utils/buildDownloadFilename'

const log = createDebug('dw:clip-download')

const MEDIA_TABS: { key: MediaType; label: string }[] = [
  { key: 'image', label: 'Images' },
  { key: 'video', label: 'Videos' },
  { key: 'audio', label: 'Audio' },
]

interface ClipDownloadModalProps {
  isOpen: boolean
  onClose: () => void
  canvasNodes: ReactFlowNode[]
  canvasEdges: ReactFlowEdge[]
}

export function ClipDownloadModal({ isOpen, onClose, canvasNodes, canvasEdges }: ClipDownloadModalProps) {
  const projectId = useProjectId()
  const [activeTab, setActiveTab] = useState<MediaType>('image')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [downloading, setDownloading] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  const allCandidates = useMemo(() => {
    if (!isOpen) return []
    const candidates = buildDownloadCandidates(canvasNodes)
    log('candidates found: %d (image=%d, video=%d, audio=%d)',
      candidates.length,
      candidates.filter(c => c.mediaType === 'image').length,
      candidates.filter(c => c.mediaType === 'video').length,
      candidates.filter(c => c.mediaType === 'audio').length,
    )
    return candidates
  }, [canvasNodes, isOpen])

  const tabCandidates = useMemo(
    () => allCandidates.filter(c => c.mediaType === activeTab),
    [allCandidates, activeTab],
  )

  const tabCounts = useMemo(() => {
    const counts: Record<MediaType, number> = { image: 0, video: 0, audio: 0 }
    for (const c of allCandidates) counts[c.mediaType]++
    return counts
  }, [allCandidates])

  const candidateKey = (c: CanvasAssetCandidate) => `${c.nodeId}:${c.assetPath}`

  const toggleSelect = useCallback((c: CanvasAssetCandidate) => {
    setSelected(prev => {
      const next = new Set(prev)
      const key = candidateKey(c)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    const keys = tabCandidates.map(candidateKey)
    const allSelected = keys.length > 0 && keys.every(k => selected.has(k))
    if (allSelected) {
      setSelected(prev => {
        const next = new Set(prev)
        keys.forEach(k => next.delete(k))
        return next
      })
    } else {
      setSelected(prev => {
        const next = new Set(prev)
        keys.forEach(k => next.add(k))
        return next
      })
    }
  }, [tabCandidates, selected])

  const selectedCount = useMemo(
    () => tabCandidates.filter(c => selected.has(candidateKey(c))).length,
    [tabCandidates, selected],
  )

  const allTabSelected = tabCandidates.length > 0 && selectedCount === tabCandidates.length

  const handleDownload = useCallback(async () => {
    if (!projectId) return

    const toDownload = allCandidates.filter(c => selected.has(candidateKey(c)))
    if (toDownload.length === 0) {
      toast.error('No assets selected')
      return
    }

    log('download start: %d assets', toDownload.length)
    setDownloading(true)
    setProgress({ done: 0, total: toDownload.length })

    const token = await getAuthToken()
    const pubToken = getPublicationToken()

    // Download each file individually via hidden iframes — same pattern as
    // useNodeActions.handleDownload. Bypasses CORS entirely (no fetch, no
    // redirect issues). Each file triggers a separate browser save.
    for (let i = 0; i < toDownload.length; i++) {
      const c = toDownload[i]
      const filename =
        c.mediaType === 'video'
          ? buildClipDownloadFilename({
              canvasNodes,
              canvasEdges,
              nodeId: c.nodeId,
              assetPath: c.assetPath,
              variantHistory: c.variantHistory ?? [],
              pickedIndex: c.pickedIndex ?? 0,
            }).filename
          : buildAssetDownloadFilename({
              canvasNodes,
              canvasEdges,
              nodeId: c.nodeId,
              assetPath: c.assetPath,
              mediaType: c.mediaType,
              variantHistory: c.variantHistory,
              pickedIndex: c.pickedIndex,
            }).filename

      const url = new URL(
        buildProjectAssetUrl(projectId, c.assetPath),
        window.location.origin,
      )
      if (token) url.searchParams.set('token', token)
      if (pubToken) url.searchParams.set('publication_token', pubToken)
      url.searchParams.set('download', filename)

      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = url.toString()
      document.body.appendChild(iframe)
      window.setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
      }, 60_000)

      log('queued %s', filename)
      setProgress({ done: i + 1, total: toDownload.length })

      if (i < toDownload.length - 1) {
        await new Promise(r => setTimeout(r, 300))
      }
    }

    setDownloading(false)
    toast.success(`Downloading ${toDownload.length} asset${toDownload.length > 1 ? 's' : ''}`)
  }, [projectId, allCandidates, selected, canvasNodes, canvasEdges])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Download Assets" size="lg">
      <div className="space-y-4">
        {/* Tab bar */}
        <div className="flex gap-1 border-b border-border">
          {MEDIA_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'px-3 py-2 text-xs font-medium transition-colors cursor-pointer',
                activeTab === tab.key
                  ? 'text-foreground border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              data-testid={`clip-tab-${tab.key}`}
            >
              {tab.label} ({tabCounts[tab.key]})
            </button>
          ))}
        </div>

        {/* Asset grid */}
        <div className="max-h-[50vh] overflow-y-auto">
          {tabCandidates.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm" data-testid="clip-empty">
              No {activeTab} assets in this project
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {tabCandidates.map(c => {
                const key = candidateKey(c)
                const isSelected = selected.has(key)
                const thumbUrl = c.mediaType === 'image' && projectId
                  ? getThumbnailUrl(projectId, c.assetPath, 200)
                  : null
                const assetUrl = (c.mediaType === 'video' || c.mediaType === 'audio') && projectId
                  ? getThumbnailUrl(projectId, c.assetPath, 0)
                  : null

                return (
                  <button
                    key={key}
                    onClick={() => toggleSelect(c)}
                    className={cn(
                      'relative rounded-lg border-2 overflow-hidden cursor-pointer transition-all group',
                      isSelected ? 'border-primary' : 'border-border hover:border-muted-foreground/40',
                    )}
                    data-testid="clip-card"
                  >
                    {/* Thumbnail / preview */}
                    {assetUrl && c.mediaType === 'audio' ? (
                      <div className="bg-muted px-3 py-3 pointer-events-none">
                        <AudioWaveformVisualization url={assetUrl} height={48} barCount={80} compact />
                      </div>
                    ) : (
                      <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
                        {thumbUrl ? (
                          <img
                            src={thumbUrl}
                            alt={c.label}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : assetUrl && c.mediaType === 'video' ? (
                          <video
                            src={assetUrl}
                            className="w-full h-full object-cover"
                            muted
                            preload="metadata"
                          />
                        ) : (
                          <span className="opacity-30 text-muted-foreground scale-150">
                            {c.mediaType === 'video' ? VIDEO_ICON : IMAGE_ICON}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Checkbox */}
                    <div className={cn(
                      'absolute top-1.5 right-1.5 w-5 h-5 rounded flex items-center justify-center',
                      isSelected ? 'bg-primary text-primary-foreground' : 'bg-black/40 text-white/60',
                    )}>
                      {isSelected && <Check className="w-3.5 h-3.5" />}
                    </div>

                    {/* Label */}
                    <div className="px-2 py-1.5 bg-card">
                      <p className="text-xs text-muted-foreground truncate">{c.label}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {tabCandidates.length > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={allTabSelected}
                onChange={toggleSelectAll}
                className="rounded border-border"
                data-testid="clip-select-all"
              />
              Select all {activeTab}s
            </label>

            <div className="flex items-center gap-3">
              {downloading && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {progress.done}/{progress.total}
                </div>
              )}
              <button
                onClick={handleDownload}
                disabled={selected.size === 0 || downloading}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-colors',
                  selected.size > 0 && !downloading
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer'
                    : 'bg-muted text-muted-foreground cursor-not-allowed',
                )}
                data-testid="clip-download-btn"
              >
                <Download className="w-3.5 h-3.5" />
                {downloading
                  ? 'Downloading...'
                  : selected.size > 0
                    ? `Download ${selected.size} asset${selected.size > 1 ? 's' : ''}`
                    : 'Select assets'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

